import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import MedicalServicesRoundedIcon from "@mui/icons-material/MedicalServicesRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import MonitorWeightRoundedIcon from "@mui/icons-material/MonitorWeightRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const offerings = [
  {
    icon: MedicalServicesRoundedIcon,
    title: "Concierge Medical Services",
    description: "Personal physician access with unlimited consultations and priority care.",
    color: "primary"
  },
  {
    icon: PsychologyRoundedIcon,
    title: "Mental Health & Therapy",
    description: "Expert psychiatrists, therapists, and counselors for complete mental wellness.",
    color: "wellness"
  },
  {
    icon: VideocamRoundedIcon,
    title: "24/7 Virtual Clinic",
    description: "Round-the-clock telehealth access to doctors from anywhere, anytime.",
    color: "trust"
  },
  {
    icon: MonitorWeightRoundedIcon,
    title: "Weight Loss & MedSpa",
    description: "Personalized weight management programs with medical supervision.",
    color: "primary"
  },
  {
    icon: MonitorHeartRoundedIcon,
    title: "Diabetes Management",
    description: "Comprehensive insulin and glucose management with daily monitoring.",
    color: "wellness"
  },
  {
    icon: AutoAwesomeRoundedIcon,
    title: "Lifestyle Wellness Plans",
    description: "Holistic programs for nutrition, fitness, and preventive healthcare.",
    color: "trust"
  }
];

export default function OfferingsGrid() {
  return (
    <Box component="section" className="section-padding" sx={{ bgcolor: "background.default" }}>
      <Box className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", maxWidth: 768, margin: "0 auto 64px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: "#e4f3ee",
              color: "#1f7f72",
              fontSize: 14,
              fontWeight: 600,
              mb: 2
            }}
          >
            Our Services
          </Box>

          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem", lg: "3rem" },
              fontWeight: 800,
              mb: 3,
              lineHeight: 1.15
            }}
          >
            Comprehensive Healthcare
            <Box component="span" className="gradient-text" sx={{ display: "block" }}>
              Tailored for You
            </Box>
          </Typography>

          <Typography sx={{ fontSize: "1.125rem", color: "text.secondary" }}>
            From preventive care to chronic disease management, our expert team provides personalized
            healthcare solutions for every stage of life.
          </Typography>
        </motion.div>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" },
            gap: 3
          }}
        >
          {offerings.map((o, index) => {
            const Icon = o.icon;
            const iconBg =
              o.color === "primary"
                ? "rgba(39,165,153,0.1)"
                : o.color === "wellness"
                ? "rgba(57,172,130,0.1)"
                : "rgba(51,144,204,0.1)";
            const iconColor =
              o.color === "primary" ? "#27a599" : o.color === "wellness" ? "#39ac82" : "#3390cc";

            return (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Box component={Link} to="/services" sx={{ display: "block", textDecoration: "none", height: "100%" }}>
                  <Box className="premium-card" sx={{ p: 4, height: "100%", cursor: "pointer" }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "16px",
                        bgcolor: iconBg,
                        color: iconColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        transition: "transform 300ms ease"
                      }}
                    >
                      <Icon sx={{ fontSize: 28 }} />
                    </Box>

                    <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 1.5, transition: "color 200ms ease" }}>
                      {o.title}
                    </Typography>

                    <Typography sx={{ color: "text.secondary", mb: 2 }}>
                      {o.description}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: 14,
                        opacity: 0,
                        transition: "opacity 250ms ease"
                      }}
                      className="offering-learn"
                    >
                      Learn more
                      <ArrowForwardRoundedIcon sx={{ ml: 0.5, fontSize: 18, transition: "transform 200ms" }} />
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
